import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GLOBAL_FILTER_EVENTS } from '@/features/global-filter/constants/events';
import { FilterSelector, FilterSelectorOption } from '@/core/components/FilterSelector/FilterSelector';
import { SelectionMap } from '@/core/components/FilterSelector/types';
import { SelectionSummary } from '@/core/components/SelectionSummary';
import { fetchAccountRepliers } from '@/core/services/ReplierService';
import { fetchConversationPagesByAccount, type ConversationPageAccountItem } from '@/core/services/ConversationPagesService';
import { fetchConversationRoutingByAccount } from '@/core/services/ConversationRoutingService';
import { BdsChipClickable, BdsIcon } from '@/core/constants/webComponents';
import { CustomTooltip } from '@/components/CustomTooltip/CustomTooltip';
import { useBdsChip } from '@/core/hooks/useBdsChip';
import {
  getInteractionLabelConfigByValue,
  getInteractionOptionsFromChannels,
  getInteractionValuesFromChannels,
} from '@/features/global-filter/constants/interactionsByChannel';
import { Channel, getChannels } from '@/core/services/ChannelsService';
import { getChannelLabelConfigByValue } from '@/core/utils/channelLabels';
import { CONFIG } from '@/config/Config';
import { type ConversationFieldName } from '../filterConfig';
import './ConversationContent.scss';

const BdsCheckbox = 'bds-checkbox' as any;

interface ConversationPublicationType {
  organic: boolean;
  darkPost: boolean;
}

interface ConversationIDChipProps {
  id: string;
  onRemove: () => void;
}

const ConversationIDChip: React.FC<ConversationIDChipProps> = ({ id, onRemove }) => {
  const ref = useRef<HTMLElement | null>(null);
  useBdsChip(ref, onRemove);

  return (
    <span className="conv-id-selector__chip-wrapper">
      <BdsChipClickable
        ref={ref as any}
        color="outline"
        close
      >
        <span className="conv-id-selector__chip-content">
          <span className="conv-id-selector__chip-text">{id}</span>
        </span>
      </BdsChipClickable>
    </span>
  );
};

export interface ConversationData {
  channels: SelectionMap;
  pages: SelectionMap;
  interactions: SelectionMap;
  criticality: SelectionMap;
  status: SelectionMap;
  respondent: SelectionMap;
  routing: SelectionMap;
  conversationId: string;
  publicationType: ConversationPublicationType;
  // Tracks whether the user has explicitly interacted with each selector. Channels, pages,
  // interactions, respondent and routing all get auto-populated with a default "select all"
  // once their options are fetched, and criticality/status start fully selected too - all
  // visually indistinguishable from an explicit "select all". These flags let the badge count
  // tell the two situations apart instead of only checking whether a selection is non-empty.
  channelsTouched: boolean;
  pagesTouched: boolean;
  interactionsTouched: boolean;
  criticalityTouched: boolean;
  statusTouched: boolean;
  respondentTouched: boolean;
  routingTouched: boolean;
}

interface ConversationContentProps {
  values?: ConversationData;
  onChange?: (values: ConversationData) => void;
  onCountChange?: (count: number) => void;
  visibleFields?: ReadonlyArray<ConversationFieldName>;
  disableOtherConversationFields?: boolean;
}

const ALWAYS_ENABLED_CONVERSATION_FIELDS: ReadonlySet<ConversationFieldName> = new Set(['status', 'respondent']);

type ConversationPagesOption = FilterSelectorOption & {
  channels: string[];
};

type OptionInput = string | FilterSelectorOption;
type PageOptionInput = string | (FilterSelectorOption & { channels?: string[] });

const normalizeChannelKey = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'intafram') return 'instagram';
  return normalized;
};

const normalizeLogoPlatform = (value: string): string => {
  const normalized = normalizeChannelKey(value);

  // BLiP DS logo names differ from API channel keys for a few platforms.
  if (normalized === 'googlemybusiness') return 'google-meu-negocio';
  if (normalized === 'googleplay') return 'google-play';
  if (normalized === 'reclameaqui') return 'reclame-aqui';

  // Threads logo is not available in the current BLiP DS logo set.
  if (normalized === 'threads') return 'instagram';

  return normalized;
};

const getSelectedChannels = (selection: SelectionMap): string[] =>
  Object.entries(selection)
    .filter(([, state]) => state === 'add')
    .map(([key]) => key);

const sortPagesByLabel = (options: ConversationPagesOption[]): ConversationPagesOption[] => {
  return [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

const filterPagesBySelectedChannels = (
  options: ConversationPagesOption[],
  selectedChannels: string[],
): ConversationPagesOption[] => {
  if (selectedChannels.length === 0) return sortPagesByLabel(options);

  const filteredOptions = options.filter((page) =>
    page.channels.some((channel) => selectedChannels.includes(channel)),
  );

  return sortPagesByLabel(filteredOptions);
};

const prepareAllSelectedMap = (options: FilterSelectorOption[]): SelectionMap => {
  const result: SelectionMap = {};
  options.forEach((option) => {
    result[option.key] = 'add';
  });
  return result;
};

const getActiveUniverseId = (): string | undefined =>
  CONFIG.cookieOrParam(CONFIG.AUTH.UNI_ID)?.split('|')[0] || undefined;

const isPageLinkedToActiveUniverse = (
  page: ConversationPageAccountItem,
  activeUniverseId?: string,
): boolean => {
  if (!activeUniverseId) return true;
  return page.linkedUniverses?.some((id) => String(id) === String(activeUniverseId)) ?? false;
};

const prepareChannelsPagesInteractionsSelection = (
  channelsArray: FilterSelectorOption[],
  normalizedPages: ConversationPagesOption[],
  allChannels: Channel[],
): { channels: SelectionMap; pages: SelectionMap; interactions: SelectionMap } => {
  const allChannelsSelected: SelectionMap = {};
  channelsArray.forEach((channel) => {
    allChannelsSelected[channel.key] = 'add';
  });

  const allPagesSelected: SelectionMap = {};
  normalizedPages.forEach((page) => {
    allPagesSelected[page.key] = 'add';
  });

  const allChannelKeys = channelsArray.map((ch) => ch.key);
  const allInteractions = getInteractionValuesFromChannels(allChannels, allChannelKeys);
  const allInteractionsSelected: SelectionMap = {};
  allInteractions.forEach((interaction) => {
    allInteractionsSelected[interaction] = 'add';
  });

  return {
    channels: allChannelsSelected,
    pages: allPagesSelected,
    interactions: allInteractionsSelected,
  };
};

const filterPageSelectionByAllowedOptions = (
  selection: SelectionMap,
  options: Array<FilterSelectorOption>,
): SelectionMap => {
  const allowedKeys = new Set(options.map((option) => option.key));
  return Object.fromEntries(
    Object.entries(selection).filter(([key]) => allowedKeys.has(key)),
  ) as SelectionMap;
};

const preparePagesForChannels = (
  pagesOptions: ConversationPagesOption[],
  channelKeys: string[],
): SelectionMap => {
  const result: SelectionMap = {};
  pagesOptions.forEach((page) => {
    const belongsToChannel = page.channels.some((ch) => channelKeys.includes(ch));
    if (belongsToChannel) {
      result[page.key] = 'add';
    }
  });
  return result;
};

const extractChannelsFromPages = (
  pagesOptions: ConversationPagesOption[],
  selectedPageKeys: string[],
): Set<string> => {
  const channelsFromPages = new Set<string>();
  selectedPageKeys.forEach((pageKey) => {
    const page = pagesOptions.find((p) => p.key === pageKey);
    page?.channels.forEach((ch) => channelsFromPages.add(ch));
  });
  return channelsFromPages;
};

const prepareChannelsMapFromSet = (
  previousChannels: SelectionMap,
  channelSet: Set<string>,
): SelectionMap => {
  const nextChannels = { ...previousChannels };
  channelSet.forEach((ch) => {
    nextChannels[ch] = 'add';
  });
  return nextChannels;
};

const normalizeSimpleOptions = (incomingOptions: OptionInput[] | undefined): FilterSelectorOption[] => {
  if (!Array.isArray(incomingOptions)) return [];

  return incomingOptions
    .map((option) => {
      if (typeof option === 'string') {
        return { key: option, label: option };
      }

      if (!option?.key || !option?.label) return null;

      return {
        key: option.key,
        label: option.label,
        description: option.description,
        iconPlatform: option.iconPlatform,
      };
    })
    .filter(Boolean) as FilterSelectorOption[];
};

const normalizePageOptions = (incomingOptions: PageOptionInput[] | undefined): ConversationPagesOption[] => {
  if (!Array.isArray(incomingOptions)) return [];

  const mapped: Array<ConversationPagesOption | null> = incomingOptions.map((option) => {
      if (typeof option === 'string') {
        return {
          key: option,
          label: option,
          channels: [],
        };
      }

      if (!option?.key || !option?.label) return null;

      return {
        key: option.key,
        label: option.label,
        description: option.description,
        iconPlatform: option.iconPlatform ? normalizeLogoPlatform(option.iconPlatform) : undefined,
        channels: Array.isArray(option.channels)
          ? option.channels.filter(Boolean)
          : [],
      };
    });

  return mapped.filter((option): option is ConversationPagesOption => option !== null);
};

export const defaultConversationValues: ConversationData = {
  channels: {},
  pages: {},
  interactions: {},
  criticality: {
    'criticality-none': 'add',
    'criticality-low': 'add',
    'criticality-medium': 'add',
    'criticality-high': 'add',
  },
  status: {
    'status-ignored': 'add',
    'status-pending': 'add',
    'status-open': 'add',
    'status-waiting': 'add',
    'status-replied': 'add',
    'status-closed': 'add',
  },
  respondent: {},
  routing: {},
  conversationId: '',
  publicationType: {
    organic: false,
    darkPost: false,
  },
  channelsTouched: false,
  pagesTouched: false,
  interactionsTouched: false,
  criticalityTouched: false,
  statusTouched: false,
  respondentTouched: false,
  routingTouched: false,
};

export const ConversationContent: React.FC<ConversationContentProps> = ({
  values = defaultConversationValues,
  onChange,
  onCountChange,
  visibleFields,
  disableOtherConversationFields = false,
}) => {
  const { t } = useTranslation();
  const resolveChannelLabel = useCallback((value: string, fallback?: string): string => {
    const { i18nKey, defaultLabel } = getChannelLabelConfigByValue(value, fallback);
    return i18nKey ? t(i18nKey, defaultLabel) : defaultLabel;
  }, [t]);
  const resolveInteractionLabel = useCallback((value: string, fallback: string): string => {
    const { i18nKey, defaultLabel } = getInteractionLabelConfigByValue(value);
    if (!i18nKey) return fallback || defaultLabel;
    return t(i18nKey, fallback || defaultLabel);
  }, [t]);
  const [data, setData] = useState<ConversationData>(values);
  const fieldIsVisible = (name: ConversationFieldName): boolean =>
    !visibleFields || visibleFields.includes(name);
  const fieldIsDisabled = (name: ConversationFieldName): boolean =>
    disableOtherConversationFields && !ALWAYS_ENABLED_CONVERSATION_FIELDS.has(name);
  const fieldGroupClassName = (name: ConversationFieldName, baseClassName: string): string => {
    const disabledSuffix = fieldIsDisabled(name) ? ` ${baseClassName}--disabled` : '';
    return baseClassName + disabledSuffix;
  };
  const organicRef = useRef<any>(null);
  const darkPostRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const [externalChannelsOptions, setExternalChannelsOptions] = useState<FilterSelectorOption[] | null>(null);
  const [externalPagesOptions, setExternalPagesOptions] = useState<ConversationPagesOption[] | null>(null);
  const [externalRespondentOptions, setExternalRespondentOptions] = useState<FilterSelectorOption[] | null>(null);
  const [externalRoutingOptions, setExternalRoutingOptions] = useState<FilterSelectorOption[] | null>(null);
  const [allChannels, setAllChannels] = useState<Channel[]>([]);
  const [channelsWithPages, setChannelsWithPages] = useState<Set<string>>(new Set());
  const [allowedPageKeys, setAllowedPageKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    let isMounted = true;

    fetchAccountRepliers()
      .then((repliers) => {
        if (!isMounted || repliers.length === 0) return;

        const options = repliers.map((replier) => ({
          key: `respondent-${replier.id}`,
          label: replier.name,
        }));

        setExternalRespondentOptions(options);

        globalThis.dispatchEvent(
          new CustomEvent(GLOBAL_FILTER_EVENTS.CONVERSATION_RESPONDENTS_OPTIONS_LOADED, {
            detail: { count: options.length },
          }),
        );

        // Pre-select all respondents if none are selected yet
        const allSelected = prepareAllSelectedMap(options);
        setData((prevData) => {
          const hasExistingSelection = Object.keys(prevData.respondent).length > 0;
          if (hasExistingSelection) return prevData;

          return {
            ...prevData,
            respondent: allSelected,
          };
        });
      })
      .catch((error) => {
        console.error('Error fetching conversation respondents:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    fetchConversationRoutingByAccount()
      .then((routingSettings) => {
        if (!isMounted || routingSettings.length === 0) return;

        const options = routingSettings.map((setting) => ({
          key: setting.id,
          label: setting.name,
        }));

        setExternalRoutingOptions(options);

        globalThis.dispatchEvent(
          new CustomEvent(GLOBAL_FILTER_EVENTS.CONVERSATION_ROUTING_OPTIONS_LOADED, {
            detail: { count: options.length },
          }),
        );

        // Pre-select all routing if none are selected yet
        const allSelected = prepareAllSelectedMap(options);
        setData((prevData) => {
          const hasExistingSelection = Object.keys(prevData.routing).length > 0;
          if (hasExistingSelection) return prevData;

          return {
            ...prevData,
            routing: allSelected,
          };
        });
      })
      .catch((error) => {
        console.error('Error fetching conversation routing options:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      fetchConversationPagesByAccount().catch(() => []),
      getChannels().catch(() => []),
    ])
      .then(([pages, allChannels]) => {
        if (!isMounted) return;

        if (pages.length === 0) {
          // Still set allChannels even if there are no pages
          setAllChannels(allChannels);
          return;
        }

        // Create a map of channel support info for smartcare filtering
        const channelSupportMap = new Map(
          allChannels.map((ch) => [ch.key.toLowerCase(), ch.support?.smartcare !== false]),
        );
        const activeUniverseId = getActiveUniverseId();

        const channelsMap = new Map<string, FilterSelectorOption>();
        const channelsWithPagesSet = new Set<string>();
        const normalizedPages = pages
          .filter((page) => {
            // Filter out channels with smartcare: false
            const channelKey = page.channel.toLowerCase();
            if (channelSupportMap.get(channelKey) === false) return false;
            return isPageLinkedToActiveUniverse(page, activeUniverseId);
          })
          .map((page) => {
            const channelKey = page.channel;
            const normalizedChannelLabel = resolveChannelLabel(channelKey);

            channelsWithPagesSet.add(channelKey);

            if (!channelsMap.has(channelKey)) {
              channelsMap.set(channelKey, {
                key: channelKey,
                label: normalizedChannelLabel,
              });
            }

            return {
              key: `${channelKey}-${page.id}`,
              label: page.title,
              description: page.username,
              iconPlatform: normalizeLogoPlatform(channelKey),
              channels: [channelKey],
            } as ConversationPagesOption;
          });

        const channelsArray = Array.from(channelsMap.values());
        const allowedKeys = new Set(normalizedPages.map((page) => page.key));
        setAllChannels(allChannels);
        setChannelsWithPages(channelsWithPagesSet);
        setAllowedPageKeys(allowedKeys);
        setExternalChannelsOptions(channelsArray);
        setExternalPagesOptions(normalizedPages);

        // Pre-select ONLY channels that have connected pages if no channels are selected yet
        const preSelection = prepareChannelsPagesInteractionsSelection(channelsArray, normalizedPages, allChannels);
        setData((prevData) => {
          const hasExistingChannelSelection = Object.keys(prevData.channels).length > 0;
          if (hasExistingChannelSelection) return prevData;

          return {
            ...prevData,
            channels: preSelection.channels,
            pages: preSelection.pages,
            interactions: preSelection.interactions,
          };
        });
      })
      .catch((error) => {
        console.error('Error fetching conversation pages/channels:', error);
      });

    return () => {
      isMounted = false;
    };
  }, [resolveChannelLabel]);

  const channelsOptions = useMemo(() => externalChannelsOptions ?? [], [externalChannelsOptions]);
  const pagesOptions = useMemo(() => externalPagesOptions ?? [], [externalPagesOptions]);
  const respondentOptions = useMemo(() => externalRespondentOptions ?? [], [externalRespondentOptions]);
  const routingOptions = useMemo(() => externalRoutingOptions ?? [], [externalRoutingOptions]);

  const selectedChannels = useMemo<string[]>(
    () => getSelectedChannels(data.channels),
    [data.channels],
  );

  const filteredPagesOptions = useMemo<FilterSelectorOption[]>(() => {
    return filterPagesBySelectedChannels(pagesOptions, selectedChannels);
  }, [pagesOptions, selectedChannels]);

  const filteredInteractionsOptions = useMemo<FilterSelectorOption[]>(() => {
    return getInteractionOptionsFromChannels(allChannels, selectedChannels, resolveInteractionLabel);
  }, [allChannels, selectedChannels, resolveInteractionLabel]);

  const criticalityOptions = useMemo<FilterSelectorOption[]>(
    () => [
      { key: 'criticality-none', label: t('globalFilter.conversationOptions.criticality.none', 'Sem criticidade') },
      { key: 'criticality-high', label: t('globalFilter.conversationOptions.criticality.high', 'Alta') },
      { key: 'criticality-medium', label: t('globalFilter.conversationOptions.criticality.medium', 'Media') },
      { key: 'criticality-low', label: t('globalFilter.conversationOptions.criticality.low', 'Baixa') },
    ],
    [t],
  );

  const statusOptions = useMemo<FilterSelectorOption[]>(
    () => [
      { key: 'status-ignored', label: t('globalFilter.conversationOptions.status.ignored', 'Ignorado') },
      { key: 'status-pending', label: t('globalFilter.conversationOptions.status.pending', 'Pendente') },
      { key: 'status-open', label: t('globalFilter.conversationOptions.status.open', 'Aberto') },
      { key: 'status-waiting', label: t('globalFilter.conversationOptions.status.waiting', 'Em espera') },
      { key: 'status-replied', label: t('globalFilter.conversationOptions.status.replied', 'Respondido') },
      { key: 'status-closed', label: t('globalFilter.conversationOptions.status.closed', 'Fechado') },
    ],
    [t],
  );

  useEffect(() => {
    const handleChannelsOptionsUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ options?: OptionInput[] }>).detail;
      const normalized = normalizeSimpleOptions(detail?.options);
      if (normalized.length > 0) {
        // Filter to include only channels that have connected pages
        const filteredChannels = normalized
          .filter((channel) => channelsWithPages.has(channel.key))
          .map((channel) => ({
            ...channel,
            label: resolveChannelLabel(channel.key, channel.label),
          }));
        if (filteredChannels.length > 0) {
          setExternalChannelsOptions(filteredChannels);
        }
      }
    };

    const handlePagesOptionsUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ options?: PageOptionInput[] }>).detail;
      const normalized = normalizePageOptions(detail?.options);
      if (normalized.length > 0) {
        const filtered = allowedPageKeys.size > 0
          ? normalized.filter((option) => allowedPageKeys.has(option.key))
          : normalized;
        setExternalPagesOptions(filtered);
      }
    };

    const handleRespondentOptionsUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ options?: OptionInput[] }>).detail;
      const normalized = normalizeSimpleOptions(detail?.options);
      if (normalized.length > 0) {
        setExternalRespondentOptions(normalized);
      }
    };

    globalThis.addEventListener(GLOBAL_FILTER_EVENTS.UPDATE_CONVERSATION_CHANNELS_OPTIONS, handleChannelsOptionsUpdate);
    globalThis.addEventListener(GLOBAL_FILTER_EVENTS.UPDATE_CONVERSATION_PAGES_OPTIONS, handlePagesOptionsUpdate);
    globalThis.addEventListener(GLOBAL_FILTER_EVENTS.UPDATE_CONVERSATION_RESPONDENTS_OPTIONS, handleRespondentOptionsUpdate);

    globalThis.dispatchEvent(new CustomEvent(GLOBAL_FILTER_EVENTS.REQUEST_CONVERSATION_CHANNELS_OPTIONS));
    globalThis.dispatchEvent(new CustomEvent(GLOBAL_FILTER_EVENTS.REQUEST_CONVERSATION_PAGES_OPTIONS));
    globalThis.dispatchEvent(new CustomEvent(GLOBAL_FILTER_EVENTS.REQUEST_CONVERSATION_RESPONDENTS_OPTIONS));

    return () => {
      globalThis.removeEventListener(GLOBAL_FILTER_EVENTS.UPDATE_CONVERSATION_CHANNELS_OPTIONS, handleChannelsOptionsUpdate);
      globalThis.removeEventListener(GLOBAL_FILTER_EVENTS.UPDATE_CONVERSATION_PAGES_OPTIONS, handlePagesOptionsUpdate);
      globalThis.removeEventListener(GLOBAL_FILTER_EVENTS.UPDATE_CONVERSATION_RESPONDENTS_OPTIONS, handleRespondentOptionsUpdate);
    };
  }, [allowedPageKeys, channelsWithPages, resolveChannelLabel]);

  useEffect(() => {
    if (!onCountChange) return;
    const isFieldFiltered = (touched: boolean, sel: SelectionMap) => touched && Object.keys(sel).length > 0;
    const count =
      (isFieldFiltered(data.channelsTouched, data.channels) ? 1 : 0) +
      (isFieldFiltered(data.pagesTouched, data.pages) ? 1 : 0) +
      (isFieldFiltered(data.interactionsTouched, data.interactions) ? 1 : 0) +
      (isFieldFiltered(data.criticalityTouched, data.criticality) ? 1 : 0) +
      (isFieldFiltered(data.statusTouched, data.status) ? 1 : 0) +
      (isFieldFiltered(data.respondentTouched, data.respondent) ? 1 : 0) +
      (isFieldFiltered(data.routingTouched, data.routing) ? 1 : 0) +
      (data.conversationId.trim().length > 0 ? 1 : 0) +
      (data.publicationType.organic || data.publicationType.darkPost ? 1 : 0);
    onCountChange(count);
  }, [data, onCountChange]);

  const updateData = useCallback(
    (updater: (previous: ConversationData) => ConversationData) => {
      setData((previous) => updater(previous));
    },
    [],
  );

  useEffect(() => {
    onChangeRef.current?.(data);
  }, [data]);

  const [typedConversationId, setTypedConversationId] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const conversationInputRef = useRef<HTMLInputElement | null>(null);

  const processTypedConversationId = (value: string) => {
    const rawText = value.trim();
    if (!rawText) return;

    const newItems = rawText
      .split(/[,\s]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (newItems.length > 0) {
      const currentItems = data.conversationId
        ? data.conversationId.split(',').map((id) => id.trim()).filter(Boolean)
        : [];

      const combinedItems = [...currentItems];
      newItems.forEach((item) => {
        if (!combinedItems.includes(item)) {
          combinedItems.push(item);
        }
      });

      const nextVal = combinedItems.join(', ');
      updateData((previous) => ({ ...previous, conversationId: nextVal }));
    }
    setTypedConversationId('');
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processTypedConversationId(typedConversationId);
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    processTypedConversationId(typedConversationId);
  };

  const handleRemoveChip = (indexToRemove: number) => {
    const currentItems = data.conversationId
      ? data.conversationId.split(',').map((id) => id.trim()).filter(Boolean)
      : [];

    const updatedItems = currentItems.filter((_, idx) => idx !== indexToRemove);
    const nextVal = updatedItems.join(', ');
    updateData((previous) => ({ ...previous, conversationId: nextVal }));
  };

  const conversationIDs = useMemo(() => {
    return data.conversationId
      ? data.conversationId.split(',').map((id) => id.trim()).filter(Boolean)
      : [];
  }, [data.conversationId]);

  useEffect(() => {
    const node = organicRef.current;
    if (!node) return;
    const handleChange = (event: Event) => {
      if (disableOtherConversationFields) return;
      const checked = (event as CustomEvent).detail?.checked ?? false;
      updateData((previous) => ({
        ...previous,
        publicationType: { ...previous.publicationType, organic: checked },
      }));
    };
    node.addEventListener('bdsChange', handleChange);
    return () => node.removeEventListener('bdsChange', handleChange);
  }, [updateData, disableOtherConversationFields]);

  useEffect(() => {
    const node = darkPostRef.current;
    if (!node) return;
    const handleChange = (event: Event) => {
      if (disableOtherConversationFields) return;
      const checked = (event as CustomEvent).detail?.checked ?? false;
      updateData((previous) => ({
        ...previous,
        publicationType: { ...previous.publicationType, darkPost: checked },
      }));
    };
    node.addEventListener('bdsChange', handleChange);
    return () => node.removeEventListener('bdsChange', handleChange);
  }, [updateData, disableOtherConversationFields]);

  const createSelectionHandler = useCallback(
    (key: 'pages' | 'interactions' | 'criticality' | 'status' | 'respondent' | 'routing') =>
      (nextSelection: React.SetStateAction<SelectionMap>) => {
        if (disableOtherConversationFields && !ALWAYS_ENABLED_CONVERSATION_FIELDS.has(key)) return;
        const touchedKey = `${key}Touched` as const;
        updateData((previous) => ({
          ...previous,
          [key]: typeof nextSelection === 'function' ? nextSelection(previous[key]) : nextSelection,
          [touchedKey]: true,
        }));
      },
    [updateData, disableOtherConversationFields],
  );

  const handleChannelsChange = useCallback(
    (nextSelection: React.SetStateAction<SelectionMap>) => {
      if (disableOtherConversationFields) return;
      updateData((previous) => {
        const nextChannels =
          typeof nextSelection === 'function' ? nextSelection(previous.channels) : nextSelection;
        const selected = getSelectedChannels(nextChannels);

        if (selected.length === 0) {
          return {
            ...previous,
            channels: nextChannels,
            pages: {},
            interactions: {},
            channelsTouched: true,
            pagesTouched: true,
            interactionsTouched: true,
          };
        }

        const nextPagesOptions = filterPagesBySelectedChannels(pagesOptions, selected);
        const nextInteractionsOptions = getInteractionOptionsFromChannels(allChannels, selected, resolveInteractionLabel);

        // When selecting channels, automatically select all pages from those channels
        const channelsBeingAdded = Object.entries(nextChannels)
          .filter(([key, action]) => action === 'add' && previous.channels[key] !== 'add')
          .map(([key]) => key);

        let nextPages = filterPageSelectionByAllowedOptions(previous.pages, nextPagesOptions);

        if (channelsBeingAdded.length > 0) {
          const pagesToAdd = preparePagesForChannels(pagesOptions, channelsBeingAdded);
          nextPages = { ...nextPages, ...pagesToAdd };
        }

        const nextInteractions = filterPageSelectionByAllowedOptions(previous.interactions, nextInteractionsOptions);

        return {
          ...previous,
          channels: nextChannels,
          pages: nextPages,
          interactions: nextInteractions,
          channelsTouched: true,
          pagesTouched: true,
          interactionsTouched: true,
        };
      });
    },
    [pagesOptions, updateData, disableOtherConversationFields, allChannels, resolveInteractionLabel],
  );

  const handlePagesChange = useCallback(
    (nextSelection: React.SetStateAction<SelectionMap>) => {
      if (disableOtherConversationFields) return;
      updateData((previous) => {
        const nextPages = typeof nextSelection === 'function' ? nextSelection(previous.pages) : nextSelection;

        // Extract channels from selected pages and auto-select them
        const selectedPageKeys = Object.entries(nextPages)
          .filter(([, action]) => action === 'add')
          .map(([key]) => key);

        const channelsFromPages = extractChannelsFromPages(pagesOptions, selectedPageKeys);
        const nextChannels = prepareChannelsMapFromSet(previous.channels, channelsFromPages);

        // Auto-select interactions for selected channels
        const selectedChannelsList = Array.from(channelsFromPages);
        const availableInteractions = getInteractionOptionsFromChannels(allChannels, selectedChannelsList, resolveInteractionLabel);
        const nextInteractions = prepareAllSelectedMap(availableInteractions);

        return {
          ...previous,
          channels: nextChannels,
          pages: nextPages,
          interactions: nextInteractions,
          channelsTouched: true,
          pagesTouched: true,
          interactionsTouched: true,
        };
      });
    },
    [updateData, disableOtherConversationFields, pagesOptions, allChannels, resolveInteractionLabel],
  );

  const handleRespondentChange = useCallback(
    (nextSelection: React.SetStateAction<SelectionMap>) => {
      updateData((previous) => ({
        ...previous,
        respondent: typeof nextSelection === 'function' ? nextSelection(previous.respondent) : nextSelection,
        respondentTouched: true,
      }));
    },
    [updateData],
  );

  const handleRoutingChange = useCallback(
    (nextSelection: React.SetStateAction<SelectionMap>) => {
      if (disableOtherConversationFields) return;
      updateData((previous) => ({
        ...previous,
        routing: typeof nextSelection === 'function' ? nextSelection(previous.routing) : nextSelection,
        routingTouched: true,
      }));
    },
    [updateData, disableOtherConversationFields],
  );

  const previousSelectionRef = useRef<Pick<ConversationData, 'channels' | 'pages' | 'respondent' | 'routing'>>({
    channels: data.channels,
    pages: data.pages,
    respondent: data.respondent,
    routing: data.routing,
  });

  useEffect(() => {
    const previous = previousSelectionRef.current;
    if (previous.channels !== data.channels) {
      globalThis.dispatchEvent(
        new CustomEvent(GLOBAL_FILTER_EVENTS.CONVERSATION_CHANNELS_CHANGED, {
          detail: { selection: data.channels },
        }),
      );
    }

    if (previous.pages !== data.pages) {
      globalThis.dispatchEvent(
        new CustomEvent(GLOBAL_FILTER_EVENTS.CONVERSATION_PAGES_CHANGED, {
          detail: { selection: data.pages },
        }),
      );
    }

    if (previous.respondent !== data.respondent) {
      globalThis.dispatchEvent(
        new CustomEvent(GLOBAL_FILTER_EVENTS.CONVERSATION_RESPONDENTS_CHANGED, {
          detail: { selection: data.respondent },
        }),
      );
    }

    if (previous.routing !== data.routing) {
      globalThis.dispatchEvent(
        new CustomEvent(GLOBAL_FILTER_EVENTS.CONVERSATION_ROUTING_CHANGED, {
          detail: { selection: data.routing },
        }),
      );
    }

    previousSelectionRef.current = {
      channels: data.channels,
      pages: data.pages,
      respondent: data.respondent,
      routing: data.routing,
    };
  }, [data.channels, data.pages, data.respondent, data.routing]);

  return (
    <div className="conversation-content">
      {fieldIsVisible('channels') && (
        <div className={fieldGroupClassName('channels', 'conversation-content__field')}>
          <FilterSelector
            label={t('globalFilter.conversationChannels')}
            placeholder={t('globalFilter.conversationChannelsPlaceholder')}
            availableItems={channelsOptions}
            selection={data.channels}
            onChange={handleChannelsChange}
            allowNegation={false}
            disabled={fieldIsDisabled('channels')}
          />
          {data.channelsTouched && <SelectionSummary selection={data.channels} />}
        </div>
      )}

      {fieldIsVisible('pages') && (
        <div className={fieldGroupClassName('pages', 'conversation-content__field')}>
          <FilterSelector
            label={t('globalFilter.conversationPages')}
            placeholder={t('globalFilter.conversationPagesPlaceholder')}
            availableItems={filteredPagesOptions}
            selection={data.pages}
            onChange={handlePagesChange}
            allowNegation={false}
            disabled={fieldIsDisabled('pages')}
          />
          {data.pagesTouched && <SelectionSummary selection={data.pages} />}
        </div>
      )}

      {fieldIsVisible('interactions') && (
        <div className={fieldGroupClassName('interactions', 'conversation-content__field')}>
          <FilterSelector
            label={t('globalFilter.conversationInteractions')}
            placeholder={t('globalFilter.conversationInteractionsPlaceholder')}
            availableItems={filteredInteractionsOptions}
            selection={data.interactions}
            onChange={createSelectionHandler('interactions')}
            allowNegation={false}
            disabled={fieldIsDisabled('interactions')}
          />
          {data.interactionsTouched && <SelectionSummary selection={data.interactions} />}
        </div>
      )}

      {fieldIsVisible('criticality') && (
        <div className={fieldGroupClassName('criticality', 'conversation-content__field')}>
          <FilterSelector
            label={t('globalFilter.conversationCriticality')}
            placeholder={t('globalFilter.conversationCriticalityPlaceholder')}
            availableItems={criticalityOptions}
            selection={data.criticality}
            onChange={createSelectionHandler('criticality')}
            allowNegation={false}
            disabled={fieldIsDisabled('criticality')}
          />
          {data.criticalityTouched && <SelectionSummary selection={data.criticality} />}
        </div>
      )}

      {fieldIsVisible('status') && (
        <div>
          <FilterSelector
            label={t('globalFilter.conversationStatus')}
            placeholder={t('globalFilter.conversationStatusPlaceholder')}
            availableItems={statusOptions}
            selection={data.status}
            onChange={createSelectionHandler('status')}
            allowNegation={false}
          />
          {data.statusTouched && <SelectionSummary selection={data.status} />}
        </div>
      )}

      {fieldIsVisible('respondent') && (
        <div>
          <FilterSelector
            label={t('globalFilter.conversationRespondent')}
            placeholder={t('globalFilter.conversationRespondentPlaceholder')}
            availableItems={respondentOptions}
            selection={data.respondent}
            onChange={handleRespondentChange}
            allowNegation={false}
          />
          {data.respondentTouched && <SelectionSummary selection={data.respondent} />}
        </div>
      )}

      {fieldIsVisible('conversationId') && (
        <div className={fieldGroupClassName('conversationId', 'conversation-content__group')}>
          <div className="conversation-content__label-wrapper">
            <span className="conversation-content__label">{t('globalFilter.conversationID')}</span>
            <CustomTooltip content={t('globalFilter.conversationIDTooltip')} position="top" maxWidth="260px">
              <BdsIcon name="info" size="x-small" />
            </CustomTooltip>
          </div>
          <label 
            className={`conv-id-selector__field${isFocused ? ' conv-id-selector__field--focused' : ''}${fieldIsDisabled('conversationId') ? ' conv-id-selector__field--disabled' : ''}`}
          >
            <div className="conv-id-selector__chips">
              {conversationIDs.map((id, index) => (
                <ConversationIDChip
                  key={`${id}-${index}`}
                  id={id}
                  onRemove={() => handleRemoveChip(index)}
                />
              ))}
              <input
                ref={conversationInputRef}
                type="text"
                className="conv-id-selector__input"
                value={typedConversationId}
                onChange={(e) => setTypedConversationId(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={handleInputBlur}
                placeholder={conversationIDs.length === 0 ? t('globalFilter.conversationIDPlaceholder') : ''}
                disabled={fieldIsDisabled('conversationId')}
              />
            </div>
          </label>
        </div>
      )}

      {fieldIsVisible('routing') && (
        <div className={fieldGroupClassName('routing', 'conversation-content__field')}>
          <FilterSelector
            label={t('globalFilter.conversationRouting')}
            placeholder={t('globalFilter.conversationRoutingPlaceholder')}
            availableItems={routingOptions}
            selection={data.routing}
            onChange={handleRoutingChange}
            allowNegation={false}
            disabled={fieldIsDisabled('routing')}
          />
          {data.routingTouched && <SelectionSummary selection={data.routing} />}
        </div>
      )}

      {fieldIsVisible('publicationType') && (
        <div className={fieldGroupClassName('publicationType', 'conversation-content__group')}>
          <span className="conversation-content__label">{t('globalFilter.conversationPublicationType')}</span>
          <div className="conversation-content__checkboxes">
            <BdsCheckbox
              ref={organicRef}
              label={t('globalFilter.conversationOrganic')}
              checked={data.publicationType.organic}
              disabled={fieldIsDisabled('publicationType')}
            />
            <BdsCheckbox
              ref={darkPostRef}
              label={t('globalFilter.conversationDarkPost')}
              checked={data.publicationType.darkPost}
              disabled={fieldIsDisabled('publicationType')}
            />
          </div>
        </div>
      )}
    </div>
  );
};
